<template>
  <div class="options-page" :class="{ dark: isDark }">
    <!-- Header cloned exactly from Chart.vue -->
    <div id="tc-options-header" v-bind:class="{ dark: isDark }" class="scrollable">
        <button class="tc-options-back" @click="$router.push('/chart')" title="Back to chart">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <span class="tc-options-searchbar" @click="onOpen">
            <span class="tc-options-searchbar-text" v-bind:class="{ dark: isDark }">{{ selectedInstrumentName }}</span>
        </span>
        <span class="tc-options-timeframe-bar">
              <span v-for="item in regularTimeframeItems" :key="item.timeframe" class="tc-options-tf-slot">
                  <button type="button"
                      :class="[
                          'tc-options-tf-btn',
                          item.className,
                          { 'tc-options-tf-active': timeframe === item.timeframe },
                          { 'tc-options-tf-hidden': item.className && item.className.includes('p-hidden') }
                      ]"
                      @click="onTimeframeSelect(item)">{{ item.label }}</button>
              </span>
              <div class="tc-options-tf-divider"></div>

          </span>
      </div>
    <div v-if="!enabled" class="options-disabled">
      Open an Indian market symbol from the chart to use Options. Indian market data access is required.
    </div>

    <main v-else class="options-shell">

      <section class="workspace-heading">
        <div><small>INDIAN MARKETS · OPTIONS WORKSPACE</small><h1>{{ selectedInstrumentName }}</h1><span>Underlying · {{ underlyingLastPrice ? formatPrice(underlyingLastPrice) : 'Price unavailable' }}</span></div>
        <label>Expiry<select v-model="selectedExpiry" @change="changeExpiry"><option v-for="expiry in expiries" :key="expiry" :value="expiry">{{ expiry }}</option></select></label>
        <div class="side-switch"><button :class="{ chosen: optionSide === 'ce' }" @click="changeSide('ce')">Calls · CE</button><button :class="{ chosen: optionSide === 'pe' }" @click="changeSide('pe')">Puts · PE</button></div>
      </section>
      <div v-if="error" class="options-error" role="alert">{{ error }}</div>
      <section class="strike-browser">
        <div><strong>Strike charts</strong><span>Selected strike in the middle · adjacent strikes on either side</span><button @click="selectDefaultStrikes(); reloadOptionCharts()" :disabled="!underlyingLastPrice">Centre on ITM</button></div>
        <nav aria-label="Select option strike"><button v-for="row in availableRows" :key="row.strike" :class="{ chosen: Number(row.strike) === Number(selectedStrike) }" @click="chooseStrike(row.strike)">{{ formatStrike(row.strike) }} <small>{{ moneyness(row.strike) }}</small></button></nav>
      </section>
      <section class="options-grid">
        <article
          v-for="panel in chartPanels"
          :key="panel.key"
          class="options-panel"
          :class="[panel.key, { active: activePanel === panel.key }]"
          :ref="'panel-' + panel.key"
          @mouseenter="setActivePanel(panel.key)"
          @focusin="setActivePanel(panel.key)"
          tabindex="0"
        >
          <div class="panel-header">
            <span>{{ panel.label }}</span>
            <strong>{{ panel.title }}</strong>
            <button v-if="panel.option" @click="addLeg(panel.option, panel.strike)">+ Add leg</button>
          </div>

          <div
            class="option-chart-pane"
            :style="{ width: panel.width + 'px', height: panel.height + 'px' }"
          >
            <div v-if="panel.status" class="option-chart-state option-chart-debug">
              <strong>{{ panel.title }}</strong>
              <span>{{ panel.status }}</span>


              <small>Timeframe: {{ timeframe }}</small>
              <small v-if="panel.candleCount">Candles: {{ panel.candleCount }}</small>
            </div>

            <tc
              v-else
              :id="'tc-options-' + panel.key"
              :key="panel.key + '-' + panel.renderKey"
              :data="panel.data"
              :width="panel.width"
              :height="panel.height"
              :title-txt="panel.title"
              :tf="timeframe"
              exchange="Indian"
              :night="isDark"
              :timezone="5.5"
              :legend-buttons="['display', 'settings', 'add', 'remove']"
              :toolbar="false"
              :chart-config="chartConfig"
              :color-back="chartColors.colorBack"
              :color-grid="chartColors.colorGrid"
              :color-text="chartColors.colorText"
              :extensions="extensions"
              :overlays="overlays"
              :x-settings="xSettings"
            />
          </div>
        </article>
      </section>
      <section class="strategy-ticket">
        <div class="ticket-heading"><div><small>STRATEGY BUILDER</small><h2>Options basket <span>{{ legs.length }} legs</span></h2></div><button :disabled="!legs.length" @click="legs = []">Clear</button></div>
        <p v-if="!legs.length">Add a strike from any chart to build a single option, spread, straddle or custom strategy.</p>
        <div v-for="(leg, index) in legs" :key="leg.id" class="ticket-leg">
          <select v-model="leg.action" aria-label="Buy or sell"><option>BUY</option><option>SELL</option></select>
          <button @click="openLeg(leg)">{{ leg.symbol }} · {{ leg.expiry }}</button>
          <label>Lots<input type="number" min="1" step="1" v-model.number="leg.lots"></label>
          <span>{{ leg.lotSize }} units / lot</span><button @click="legs.splice(index, 1)" aria-label="Remove leg">Remove</button>
        </div>
        <footer><span>Draft basket · Broker execution is not connected. Prices and margin must be verified with your broker.</span><button :disabled="!validBasket" @click="exportBasket">Export order basket</button></footer>
      </section>
    </main>

    <!-- Symbol Search Component -->
    <SymbolSearch
      ref="symbolSearch"
      @select="selectInstrument"
    />
  </div>
</template>

<script>
import Tc from '../Tc.vue';
import Data from '../../data/data.json';
import DataCube from '../helpers/datacube.js';
import Extensions from '../index_dev';
import Overlays from '../overlay_prod';
import Utils from '../stuff/utils';
import indianMarketApi from '../services/indian_market_service';
import SymbolSearch from './SymbolSearch.vue';

export default {
  name: 'TradingCandleOptions',
  components: { Tc, SymbolSearch },
  data() {
    return {
      optionSide: 'ce',
      expiries: [],
      legs: [],
      requestIds: {},
      selectionVersion: 0,
      timeframe: '5m',
      timeframes: ['1m', '3m', '5m', '15m', '30m', '1H', '2H', '4H', '1D'],
      searchQuery: 'NIFTY',
      searchResults: [],
      showSearch: false,
      allSymbols: {},
      selectedInstrument: null,
      selectedExpiry: '',
      chainRows: [],
      selectedCeStrike: null,
      selectedPeStrike: null,
      ceStrikeQuery: '',
      peStrikeQuery: '',
      searchTimer: null,
      resizeObserver: null,
      defaultLoaded: false,
      charts: { ce: null, underlying: null, pe: null },
      loading: { ce: false, underlying: false, pe: false },
      chartErrors: { ce: '', underlying: '', pe: '' },
      renderErrors: { ce: '', underlying: '', pe: '' },
      chartMeta: { ce: {}, underlying: {}, pe: {} },
      panelMetrics: { ce: { width: 420, height: 520 }, underlying: { width: 420, height: 520 }, pe: { width: 420, height: 520 } },
      underlyingLastPrice: null,
      error: '',
      activePanel: 'underlying',
      currentChartType: 'candles',
      extensions: Object.values(Extensions),
      overlays: Object.values(Overlays),
    };
  },
  computed: {
    enabled() {
      return !!(this.$store && this.$store.getters.indianMarketDataEnabled && /^(NSE|BSE)[^|]*\|/.test(this.$route.query.instrument || ''));
    },
    isDark() {
      return this.$store && this.$store.getters.isDarkTheme;
    },
    selectedInstrumentName() {
      return this.selectedInstrument
        ? (this.selectedInstrument.trading_symbol || this.selectedInstrument.symbol || this.selectedInstrument.name)
        : 'Select instrument';
    },
    ceOption() {
      const row = this.chainRows.find(r => Number(r.strike) === Number(this.selectedCeStrike));
      return row && row.ce;
    },
    peOption() {
      const row = this.chainRows.find(r => Number(r.strike) === Number(this.selectedPeStrike));
      return row && row.pe;
    },
    instrumentSearchResults() {
      return this.searchResults.filter(item => this.isStockOrIndexInstrument(item));
    },
    regularTimeframeItems() {
      return this.timeframes.map(tf => ({
        timeframe: tf,
        label: tf,
        className: ''
      }));
    },
    ceStrikeRows() {
      return this.chainRows.filter(row => row.ce);
    },
    peStrikeRows() {
      return this.chainRows.filter(row => row.pe);
    },
    chartColors() {
      return this.isDark
        ? { colorBack: '#131722', colorGrid: '#1e222d', colorText: '#787b86' }
        : { colorBack: '#ffffff', colorGrid: '#e0e3eb', colorText: '#131722' };
    },
    chartConfig() {
      return {
        DEFAULT_LEN: 50,
        MINIMUM_LEN: 20
      };
    },
    xSettings() {
      return {
        'grid-resize': { min_height: 80 }
      };
    },
    selectedStrike() { return this.optionSide === 'ce' ? this.selectedCeStrike : this.selectedPeStrike; },
    availableRows() { return this.chainRows.filter(row => row[this.optionSide]).slice().sort((a, b) => a.strike - b.strike); },
    strikePanels() {
      const rows = this.availableRows;
      const index = rows.findIndex(row => Number(row.strike) === Number(this.selectedStrike));
      return ['ce', 'underlying', 'pe'].map((key, offset) => {
        const row = index >= 0 ? rows[index + offset - 1] : null;
        return { key, strike: row && row.strike, option: row && row[this.optionSide], label: ['Lower strike', 'Selected strike', 'Higher strike'][offset] };
      });
    },
    validBasket() { return this.legs.length > 0 && this.legs.every(leg => Number.isInteger(leg.lots) && leg.lots > 0 && leg.lotSize > 0); },
    chartPanels() {
      return this.strikePanels.map(panel => ({ ...this.buildChartPanel(panel.key, panel.label, panel.option ? panel.option.trading_symbol : 'No adjacent strike'), ...panel }));
    }
  },
  watch: {
    enabled: { immediate: true, handler(value) { if (value) this.loadDefaultInstrument(); } }
  },
  mounted() {
    window.addEventListener('resize', this.updatePanelSize);
    this.$nextTick(() => {
      this.updatePanelSize();
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.updatePanelSize);
        ['ce', 'underlying', 'pe'].forEach(key => {
          const el = this.getPanelElement(key);
          if (el) this.resizeObserver.observe(el);
        });
      }
    });
    if (this.enabled) this.loadDefaultInstrument();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updatePanelSize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  },
  errorCaptured(error, vm, info) {
    const message = error && error.message ? error.message : 'Chart render failed.';
    const key = this.resolveChartKeyFromVm(vm);
    if (key) {
      this.$set(this.renderErrors, key, `Chart render error: ${message}`);
    } else {
      this.error = `Options render error: ${message}`;
    }
    console.error('[Options] render error:', info, error);
    return false;
  },
  methods: {
    getPanelElement(key) {
      const ref = this.$refs['panel-' + key];
      return Array.isArray(ref) ? ref[0] : ref;
    },
    updatePanelSize() {
      this.$nextTick(() => {
        ['ce', 'underlying', 'pe'].forEach(key => {
          const el = this.getPanelElement(key);
          const width = el ? Math.floor(el.clientWidth) : Math.floor((window.innerWidth - 36) / 3);
          const height = Math.max(360, window.innerHeight * 0.48);
          this.$set(this.panelMetrics, key, {
            width: Math.max(240, width - 2),
            height
          });
        });
      });
    },
    resolveChartKeyFromVm(vm) {
      let cursor = vm;
      while (cursor) {
        const id = cursor.$props && cursor.$props.id;
        if (typeof id === 'string' && id.indexOf('tc-options-') === 0) {
          return id.replace('tc-options-', '');
        }
        cursor = cursor.$parent;
      }
      return '';
    },
    candleCount(key) {
      const cube = this.charts[key];
      return cube && cube.data && cube.data.chart && Array.isArray(cube.data.chart.data)
        ? cube.data.chart.data.length
        : 0;
    },
    chartStatus(key) {
      if (this.loading[key]) return 'Loading chart...';
      if (this.chartErrors[key]) return this.chartErrors[key];
      if (this.renderErrors[key]) return this.renderErrors[key];
      if (!this.charts[key]) return key === 'underlying' ? 'No instrument chart loaded.' : 'No option chart loaded.';
      if (!this.candleCount(key)) return 'Chart data loaded, but candle list is empty.';
      return '';
    },
    buildChartPanel(key, label, title) {
      const metrics = this.panelMetrics[key] || { width: 420, height: 520 };
      return {
        key,
        label,
        title,
        data: this.charts[key],
        width: metrics.width,
        height: metrics.height,
        status: this.chartStatus(key),
        meta: this.chartMeta[key],
        candleCount: this.candleCount(key),
        renderKey: [
          this.timeframe,
          title,
          this.chartMeta[key] && this.chartMeta[key].instrumentKey,
          this.candleCount(key)
        ].join('|')
      };
    },
    isStockOrIndexInstrument(item) {
      const type = String(item.instrument_type || item.instrumentType || item.type || '').toUpperCase();
      const segment = String(item.segment || item.exchange || item.exch || '').toUpperCase();
      const key = String(item.instrument_key || '').toUpperCase();
      const symbol = String(item.trading_symbol || item.symbol || item.name || '').toUpperCase();
      if (key.includes('INDEX') || segment.includes('INDEX') || type.includes('INDEX')) return true;
      if (type === 'EQ' || type === 'STOCK' || type === 'EQUITY') return true;
      if (segment.includes('NSE_EQ') || segment === 'NSE') return !/(CE|PE|FUT)$/.test(symbol);
      return false;
    },
    onOpen() {
      const currentDisplay = this.selectedInstrumentName
        .replace(/-EQ$/, '')
        .replace(/-INDEX$/, '')
        .replace(/-FUT$/, '');
      if (this.$refs.symbolSearch) {
        this.$refs.symbolSearch.show(currentDisplay === 'Select instrument' ? 'NIFTY' : currentDisplay);
      }
    },
    cloneDefaultDataset() {
      const dataset = JSON.parse(JSON.stringify(Data));
      dataset.onchart = Array.isArray(dataset.onchart) ? dataset.onchart : [];
      dataset.offchart = Array.isArray(dataset.offchart) ? dataset.offchart : [];
      dataset.tools = Array.isArray(dataset.tools) ? dataset.tools : [];
      dataset.tool = dataset.tool || {};
      this.applyPersistedDefaultIndicatorGroups(dataset);
      return dataset;
    },
    readSavedIndicatorGroups() {
      try {
        const groups = JSON.parse(localStorage.getItem('tc_saved_indicator_groups') || '[]');
        return Array.isArray(groups) ? groups : [];
      } catch (error) {
        return [];
      }
    },
    overlayGroupName(overlay) {
      if (!overlay) return undefined;
      return (overlay.settings && overlay.settings.group) || overlay.group;
    },
    findDefaultOverlayTemplate(indicator) {
      const sides = ['onchart', 'offchart'];
      for (const side of sides) {
        const list = Data[side] || [];
        const found = list.find(ov =>
          ov.type === indicator.type ||
          (indicator.name && ov.name === indicator.name)
        );
        if (found) return { side, overlay: found };
      }
      return null;
    },
    applyPersistedDefaultIndicatorGroups(dataset) {
      const defaultGroups = this.readSavedIndicatorGroups().filter(group =>
        group && group.isDefault === true && Array.isArray(group.indicators)
      );
      if (!defaultGroups.length) return;

      for (const group of defaultGroups) {
        const groupName = group.name || 'VT';
        const removeNames = new Set(['VT', groupName]);
        ['onchart', 'offchart'].forEach(side => {
          dataset[side] = (dataset[side] || []).filter(ov => !removeNames.has(this.overlayGroupName(ov)));
        });

        for (const indicator of group.indicators) {
          if (!indicator || indicator.hidden) continue;
          const template = this.findDefaultOverlayTemplate(indicator);
          const side = indicator.side === 'offchart'
            ? 'offchart'
            : (template && template.side === 'offchart' ? 'offchart' : 'onchart');
          const base = template
            ? JSON.parse(JSON.stringify(template.overlay))
            : { name: indicator.name || indicator.type, type: indicator.type, data: [], settings: {} };
          base.name = indicator.name && indicator.name !== indicator.type
            ? indicator.name
            : (base.name || indicator.type);
          base.type = indicator.type || base.type;
          base.group = groupName;
          base.data = [];
          base.settings = {
            ...(base.settings || {}),
            ...(indicator.settings || {}),
            group: groupName
          };
          dataset[side].push(base);
        }
      }
    },
    async loadDefaultInstrument() {
      if (this.defaultLoaded) return;
      this.defaultLoaded = true;
      this.error = '';
      try {
        const query = this.$route.query;
        await this.selectInstrument({ instrument_key: query.instrument, trading_symbol: query.symbol, name: query.name, exchange: query.exchange, instrument_type: 'EQ', type: 'IndianMarket' });
      } catch (error) {
        console.error('[Options] default instrument load failed:', error);
        this.defaultLoaded = false;
        this.error = 'Unable to load Indian market instruments.';
      }
    },
    onSearchInput() {
      this.showSearch = true;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.searchInstruments, 220);
    },
    async searchInstruments() {
      if (!this.searchQuery || this.searchQuery.length < 1) {
        this.searchResults = [];
        return;
      }
      try {
        this.searchResults = await indianMarketApi.searchSymbols(this.searchQuery, 30);
      } catch (error) {
        console.error('[Options] search failed:', error);
        this.searchResults = [];
      }
    },
    async selectInstrument(item) {
      if (!item || !/^(NSE|BSE)[^|]*\|/.test(item.instrument_key || '') || !this.isStockOrIndexInstrument(item)) {
        this.error = 'Select an Indian equity or index underlying.';
        return;
      }
      const version = ++this.selectionVersion;
      this.selectedInstrument = item;
      this.selectedExpiry = '';
      this.chainRows = [];
      this.invalidateCharts();
      this.underlyingLastPrice = null;
      this.legs = [];
      this.error = '';
      await this.loadOptionChain();
      if (version !== this.selectionVersion) return;
      await this.loadUnderlyingChart();
      if (version !== this.selectionVersion) return;
      this.selectDefaultStrikes();
      await this.reloadOptionCharts();
      this.updatePanelSize();
    },
    async loadOptionChain() {
      if (!this.selectedInstrument) return;
      const version = this.selectionVersion;
      const candidates = this.getUnderlyingCandidates(this.selectedInstrument);
      let chain = null;
      let lastError = '';
      for (const underlying of candidates) {
        try {
          chain = await indianMarketApi.loadOptionChain(underlying, this.selectedExpiry);
          if (chain && Array.isArray(chain.rows) && chain.rows.length) break;
        } catch (error) {
          lastError = error && error.message ? error.message : 'Failed to load option chain.';
          console.error('[Options] option chain failed for', underlying, error);
        }
      }
      if (!chain) chain = { rows: [], expiry: '', expiries: [] };
      if (version !== this.selectionVersion) return;
      this.expiries = chain.expiries || [];
      this.chainRows = chain.rows || [];
      this.selectedExpiry = chain.expiry || '';
      if (!this.chainRows.length) {
        this.error = lastError || 'No option strikes found for this instrument.';
        this.selectedCeStrike = null;
        this.selectedPeStrike = null;
        this.syncStrikeQueries();
      }
    },
    selectDefaultStrikes() {
      if (!this.chainRows.length) return;
      const price = Number(this.underlyingLastPrice);
      if (!Number.isFinite(price) || price <= 0) {
        const middle = this.chainRows[Math.floor(this.chainRows.length / 2)];
        this.selectedCeStrike = middle && middle.ce ? middle.strike : this.firstStrikeWith('ce');
        this.selectedPeStrike = middle && middle.pe ? middle.strike : this.firstStrikeWith('pe');
        this.syncStrikeQueries();
        return;
      }
      const ceRows = this.chainRows.filter(r => r.ce);
      const peRows = this.chainRows.filter(r => r.pe);
      const ceItm = ceRows.filter(r => r.strike < price).sort((a, b) => Math.abs(b.strike - price) - Math.abs(a.strike - price)).pop();
      const peItm = peRows.filter(r => r.strike > price).sort((a, b) => Math.abs(b.strike - price) - Math.abs(a.strike - price)).pop();
      this.selectedCeStrike = (ceItm || ceRows[0] || {}).strike || null;
      this.selectedPeStrike = (peItm || peRows[0] || {}).strike || null;
      this.syncStrikeQueries();
    },
    firstStrikeWith(side) {
      const row = this.chainRows.find(r => r[side]);
      return row ? row.strike : null;
    },
    getUnderlyingCandidates(item) {
      const raw = [
        item.trading_symbol,
        item.symbol,
        item.name,
        item.formatted_ins_name,
        item.instrument_key && item.instrument_key.split('|').pop()
      ].filter(Boolean);
      const normalized = raw.map(value => String(value)
        .toUpperCase()
        .replace(/NIFTY\s*50/g, 'NIFTY')
        .replace(/NIFTY50/g, 'NIFTY')
        .replace(/BANK\s*NIFTY/g, 'BANKNIFTY')
        .replace(/FIN\s*NIFTY/g, 'FINNIFTY')
        .replace(/-EQ/g, '')
        .replace(/\s+EQ$/g, '')
        .replace(/EQUITY/g, '')
        .replace(/-INDEX/g, '')
        .replace(/INDEX/g, '')
        .replace(/[^A-Z0-9]/g, '')
      ).filter(Boolean);
      return [...new Set([...raw, ...normalized])];
    },
    onTimeframeSelect(item) {
      return this.setTimeframe(item.timeframe);
    },
    async setTimeframe(tf) {
      if (this.timeframe === tf) return;
      this.timeframe = tf;
      await this.reloadAllCharts();
    },
    async onStrikeChange() {
      await this.reloadOptionCharts();
    },
    syncStrikeQueries() {
      this.ceStrikeQuery = this.selectedCeStrike !== null && this.selectedCeStrike !== undefined
        ? this.formatStrikePlain(this.selectedCeStrike)
        : '';
      this.peStrikeQuery = this.selectedPeStrike !== null && this.selectedPeStrike !== undefined
        ? this.formatStrikePlain(this.selectedPeStrike)
        : '';
    },
    parseStrikeInput(value) {
      const cleaned = String(value || '').replace(/,/g, '').trim();
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    },
    findNearestStrike(rows, value) {
      if (!rows.length || value === null) return null;
      return rows
        .slice()
        .sort((a, b) => Math.abs(Number(a.strike) - value) - Math.abs(Number(b.strike) - value))[0];
    },
    async commitStrikeSearch(side) {
      const selectedValue = side === 'ce' ? this.selectedCeStrike : this.selectedPeStrike;
      if (!selectedValue) return;

      const rows = side === 'ce' ? this.ceStrikeRows : this.peStrikeRows;
      const row = this.findNearestStrike(rows, selectedValue);
      if (!row) return;

      if (side === 'ce') {
        this.selectedCeStrike = row.strike;
      } else {
        this.selectedPeStrike = row.strike;
      }
      await this.reloadOptionCharts();
    },
    setActivePanel(key) {
      this.activePanel = key;
      this.$nextTick(() => {
        const el = this.getPanelElement(key);
        if (el && typeof el.focus === 'function') {
          el.focus({ preventScroll: true });
        }
      });
    },
    invalidateCharts() {
      ['ce', 'underlying', 'pe'].forEach(key => {
        this.requestIds[key] = (this.requestIds[key] || 0) + 1;
        this.$set(this.charts, key, null);
        this.$set(this.loading, key, false);
      });
    },
    async changeExpiry() {
      ++this.selectionVersion;
      this.invalidateCharts();
      this.legs = [];
      this.error = '';
      await this.loadOptionChain();
      this.selectDefaultStrikes();
      await this.reloadOptionCharts();
    },
    changeSide(side) { this.optionSide = side; this.reloadOptionCharts(); },
    chooseStrike(strike) {
      if (this.optionSide === 'ce') this.selectedCeStrike = strike;
      else this.selectedPeStrike = strike;
      this.reloadOptionCharts();
    },
    moneyness(strike) {
      if (!this.underlyingLastPrice) return '';
      if (Number(strike) === Number(this.underlyingLastPrice)) return 'ATM';
      return (this.optionSide === 'ce' ? strike < this.underlyingLastPrice : strike > this.underlyingLastPrice) ? 'ITM' : 'OTM';
    },
    addLeg(option, strike) {
      this.legs.push({ id: Date.now() + '-' + this.legs.length, instrumentKey: option.instrument_key, symbol: option.trading_symbol, strike, side: this.optionSide, expiry: this.selectedExpiry, action: 'BUY', lots: 1, lotSize: Number(option.lotSize || option.lot_size || 0) });
    },
    openLeg(leg) { this.optionSide = leg.side; this.chooseStrike(leg.strike); },
    exportBasket() {
      if (!this.validBasket) return;
      const url = URL.createObjectURL(new Blob([JSON.stringify({ version: 1, status: 'draft', underlying: this.selectedInstrument, legs: this.legs.map(leg => ({ ...leg, quantity: leg.lots * leg.lotSize })) }, null, 2)], { type: 'application/json' }));
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = 'tradingcandle-options-basket.json'; anchor.click(); URL.revokeObjectURL(url);
    },
    async reloadAllCharts() { await this.reloadOptionCharts(); },
    async reloadOptionCharts() { await Promise.all(this.strikePanels.map(panel => this.loadChart(panel.key, panel.option))); },
    async loadUnderlyingChart() {
      const instrument = this.selectedInstrument;
      const candles = await indianMarketApi.loadHistoricalData(instrument.instrument_key, this.timeframe);
      if (instrument === this.selectedInstrument && candles.length) this.underlyingLastPrice = candles[candles.length - 1][4];
    },
    async loadChart(slot, symbol) {
      const requestId = (this.requestIds[slot] || 0) + 1;
      this.requestIds[slot] = requestId;
      if (!symbol || !symbol.instrument_key) {
        this.$set(this.loading, slot, false);
        this.$set(this.charts, slot, null);
        this.$set(this.chartErrors, slot, slot === 'underlying' ? 'No instrument selected.' : 'No strike selected for this side.');
        this.$set(this.chartMeta, slot, {});
        return;
      }
      this.$set(this.loading, slot, true);
      this.$set(this.chartErrors, slot, '');
      this.$set(this.renderErrors, slot, '');
      this.$set(this.chartMeta, slot, {
        instrumentKey: symbol.instrument_key,
        requestedTimeframe: this.timeframe,
        tradingSymbol: symbol.trading_symbol || symbol.symbol || symbol.name || ''
      });
      try {
        const candles = await indianMarketApi.loadHistoricalData(symbol.instrument_key, this.timeframe);
        if (this.requestIds[slot] !== requestId) return;
        if (!Array.isArray(candles) || !candles.length) {
          this.$set(this.charts, slot, null);
          this.$set(this.chartErrors, slot, 'API returned no candles for this symbol/timeframe.');
          return;
        }

        this.$set(this.charts, slot, this.toDataCube(candles));
      } catch (error) {
        if (this.requestIds[slot] !== requestId) return;
        console.error('[Options] chart load failed:', error);
        this.$set(this.charts, slot, null);
        this.$set(this.chartErrors, slot, error && error.message ? error.message : 'Failed to load chart data.');
      } finally {
        if (this.requestIds[slot] === requestId) this.$set(this.loading, slot, false);
      }
    },
    toDataCube(candles) {
      if (!Array.isArray(candles) || !candles.length) return null;
      const dataset = this.cloneDefaultDataset();
      dataset.chart.type = 'Candles';
      dataset.chart.indexBased = true;
      dataset.chart.name = 'Indian Market';
      dataset.chart.exchange = 'Indian';
      dataset.chart.tf = this.timeframe;
      dataset.chart.interval_ms = Utils.parse_tf(this.timeframe);
      dataset.chart.data = candles.map(c => [
        Number(c[0]), Number(c[1]), Number(c[2]), Number(c[3]), Number(c[4]), Number(c[5] || 0)
      ]);
      dataset.chart.grid = dataset.chart.grid || { logScale: false };
      dataset.chart.settings = {
        ...(dataset.chart.settings || {}),
        'z-index': -2,
        colorCandleUp: 'rgb(38, 166, 154)',
        colorCandleDw: 'rgb(239, 83, 80)'
      };
      return new DataCube(dataset);
    },
    formatStrike(value) {
      return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    },
    formatStrikePlain(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return '';
      return Number.isInteger(num) ? String(num) : String(num);
    },
    formatPrice(value) {
      return Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    onChartTypeChanged(chartType) {
      this.currentChartType = chartType;
      // Visual only - don't modify actual charts
    },
    openAddIndicator() {
    }
  }
};
</script>

<style scoped>
.options-page { --surface:#fff; --bg:#f4f6fa; --line:#dce1ea; --text:#202b40; --muted:#69758b; --accent:#395ce5; background:var(--bg); color:var(--text); min-height:100vh; font:13px system-ui,sans-serif; }
.options-page.dark { --surface:#171d29; --bg:#10141e; --line:#2b3446; --text:#e0e6f1; --muted:#98a5ba; --accent:#8aa1ff; }
button,select,input { font:inherit; color:inherit; background:var(--surface); border:1px solid var(--line); border-radius:6px; padding:8px 11px; }
button { cursor:pointer; } button:disabled { opacity:.45; cursor:default; } button:hover:not(:disabled),button.chosen { border-color:var(--accent); color:var(--accent); } button:focus-visible,select:focus-visible,input:focus-visible { outline:2px solid var(--accent); outline-offset:2px; }
#tc-options-header { display:flex; gap:18px; align-items:center; padding:10px 16px; background:var(--surface); border-bottom:1px solid var(--line); overflow:auto; }
.tc-options-searchbar { cursor:pointer; font-weight:650; white-space:nowrap; }.tc-options-timeframe-bar { display:flex; gap:4px; }.tc-options-tf-active { color:var(--accent); }
.options-shell { padding:20px; display:grid; gap:16px; }.workspace-heading { display:flex; gap:24px; align-items:center; flex-wrap:wrap; }.workspace-heading>div:first-child { flex:1; }h1 { margin:5px 0; font-size:25px; letter-spacing:-.7px; }small,.workspace-heading span { color:var(--muted); }label { display:flex; align-items:center; gap:10px; }.side-switch { display:flex; gap:5px; }
.strike-browser,.strategy-ticket { background:var(--surface); border:1px solid var(--line); border-radius:10px; padding:14px; min-width:0; }.strike-browser>div { display:flex; align-items:center; gap:14px; margin-bottom:12px; }.strike-browser span { flex:1; color:var(--muted); }.strike-browser nav { display:flex; gap:6px; overflow:auto; max-height:90px; }.strike-browser nav button { flex-shrink:0; }.strike-browser small { display:block; font-size:10px; margin-top:3px; }
.options-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; }.options-panel { overflow:hidden; background:var(--surface); border:1px solid var(--line); border-radius:9px; min-width:0; }.options-panel.underlying { border-color:var(--accent); }.panel-header { display:flex; flex-wrap:wrap; gap:8px; align-items:center; padding:10px; min-height:50px; border-bottom:1px solid var(--line); }.panel-header>span { width:100%; color:var(--muted); font-size:11px; }.panel-header strong { flex:1; font-size:12px; }.option-chart-state { display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; gap:10px; padding:15px; box-sizing:border-box; color:var(--muted); text-align:center; }
.ticket-heading, .strategy-ticket footer,.ticket-leg { display:flex; align-items:center; gap:12px; }.ticket-heading>div { flex:1; }h2 { font-size:17px; margin:6px 0 14px; }h2 span { color:var(--muted); font-size:12px; font-weight:400; margin-left:8px; }.ticket-leg { border-top:1px solid var(--line); padding:10px 0; flex-wrap:wrap; }.ticket-leg>button:nth-child(2) { flex:1; text-align:left; }.ticket-leg input { width:58px; }.strategy-ticket footer { border-top:1px solid var(--line); padding-top:14px; margin-top:10px; }.strategy-ticket footer span { flex:1; color:var(--muted); font-size:12px; }.options-error,.options-disabled { padding:16px; color:#d56b53; }
@media(max-width:1000px) { .options-grid { grid-template-columns:1fr; }.options-shell { padding:10px; }.strike-browser>div { flex-wrap:wrap; }.strategy-ticket footer { flex-wrap:wrap; } }
</style>
